import {Card, FormControl, FormField, Option} from 'platform/components';
import {Grid, GridItem, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isFalse, isNotNil, isTrue} from 'ramda-adjunct';

import {DefaultPaymentType} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {DocumentTemplateOptionGroup} from '@dms/settings/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {WarehouseDirectSaleVariantsDetailFormSchemaType} from '../../../types/WarehouseDirectSaleVariantsDetailFormSchema';
import {AutoInitiatePayment} from './AutoInitiatePayment';

const FIELDS_TO_CLEAR = [
  'invoice.docSeriesId',
  'invoice.templateId',
  'deposit.docSeriesId',
  'invoiceCorrection.docSeriesId',
  'taxDocument.docSeriesId',
  'taxDocumentCorrection.docSeriesId',
  'invoiceEu.docSeriesId',
  'invoiceEuCorrection.docSeriesId',
  'invoiceOutsideEu.docSeriesId',
  'invoiceOutsideEuCorrection.docSeriesId',
] as const;

interface PaymentCardProps extends TestIdProps {
  title: string;
  paymentType: DefaultPaymentType;
  path: 'cardPayment' | 'cashPayment' | 'cashOnDeliveryPayment' | 'bankPayment';
  control: FormControl<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  formApi: UseFormReturn<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  invoiceTemplateOptions: DocumentTemplateOptionGroup[];
  invoiceDocSeriesOptions: Option[];
}

export function PaymentCard(props: PaymentCardProps) {
  const isPaymentTypeEnabled = useWatch({
    control: props.control,
    name: `documents.${props.path}.isEnable`,
  });
  const defaultPaymentType = useWatch({name: 'documents.defaultPaymentType'});

  const autoInitiatePaymentPath = match(props.path)
    .with('cashPayment', 'cardPayment', (path) => path)
    .otherwise(() => null);

  const handlePaymentTypeToggle = (value: boolean) => {
    const paymentTypeIsDefault = props.paymentType === defaultPaymentType;

    props.formApi.setValue(`documents.${props.path}.isEnable`, value, {shouldDirty: true});

    if (isFalse(value)) {
      clearPaymentFields(props.formApi);

      if (paymentTypeIsDefault) {
        props.formApi.setValue('documents.defaultPaymentType', null);
      }
    }
  };

  const clearPaymentFields = (
    formApi: UseFormReturn<WarehouseDirectSaleVariantsDetailFormSchemaType>
  ) => {
    FIELDS_TO_CLEAR.forEach((field) => {
      formApi.setValue(`documents.${props.path}.${field}`, null);
    });
  };

  return (
    <Card
      title={props.title}
      variant="inlineGrey"
      isExpanded={isTrue(isPaymentTypeEnabled)}
      control={{
        type: 'switch',
        value: isPaymentTypeEnabled,
        onChange: handlePaymentTypeToggle,
      }}
      data-testid={suffixTestId('card', props)}
    >
      <VStack spacing={4}>
        <Grid columns={2}>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoice.templateId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceTemplate')}
              options={props.invoiceTemplateOptions}
              data-testid={suffixTestId('docSeriesId', props)}
            />
          </GridItem>
        </Grid>

        <Grid columns={2}>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.deposit.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.proformaInvoiceDocumentSerie')}
              options={props.invoiceDocSeriesOptions}
              data-testid={suffixTestId('docSeriesId', props)}
            />
          </GridItem>
        </Grid>

        <Grid columns={2}>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoice.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceDocumentSerie')}
              options={props.invoiceDocSeriesOptions}
              data-testid={suffixTestId('invoiceTemplateId', props)}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoiceCorrection.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceCorrectionDocumentSerie')}
              options={props.invoiceDocSeriesOptions}
              data-testid={suffixTestId('docSeriesId', props)}
            />
          </GridItem>
        </Grid>

        <Grid columns={2}>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.taxDocument.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.taxDocumentDocumentSerie')}
              options={props.invoiceDocSeriesOptions}
              data-testid={suffixTestId('invoiceTemplateId', props)}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.taxDocumentCorrection.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.taxDocumentCorrectionDocumentSerie')}
              options={props.invoiceDocSeriesOptions}
              data-testid={suffixTestId('docSeriesId', props)}
            />
          </GridItem>
        </Grid>

        <Grid columns={2}>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoiceEu.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceEuDocumentSerie')}
              options={props.invoiceDocSeriesOptions}
              data-testid={suffixTestId('invoiceTemplateId', props)}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoiceEuCorrection.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceEuCorrectionDocumentSerie')}
              options={props.invoiceDocSeriesOptions}
              data-testid={suffixTestId('docSeriesId', props)}
            />
          </GridItem>
        </Grid>

        <Grid columns={2}>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoiceOutsideEu.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceOutsideEuDocumentSerie')}
              options={props.invoiceDocSeriesOptions}
              data-testid={suffixTestId('invoiceTemplateId', props)}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoiceOutsideEuCorrection.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceOutsideEuCorrectionDocumentSerie')}
              options={props.invoiceDocSeriesOptions}
              data-testid={suffixTestId('docSeriesId', props)}
            />
          </GridItem>
        </Grid>

        <Show when={isNotNil(autoInitiatePaymentPath)}>
          <AutoInitiatePayment
            control={props.control}
            path={autoInitiatePaymentPath}
            data-testid={suffixTestId('autoInitiatePayment', props)}
          />
        </Show>
      </VStack>
    </Card>
  );
}
